import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicyPage = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy - The Host Bros</title>
                <meta name="description" content="Privacy policy for The Host Bros web hosting services." />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
                            <p className="text-gray-600">
                                <strong>Effective Date:</strong> [Insert Date]
                            </p>
                        </div>

                        <div className="prose prose-gray max-w-none">
                            <p className="text-lg text-gray-700 mb-8">
                                No Big Deal Group LLC ("Company", "we", "us", or "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services, including our billing process.
                            </p>

                            <div className="space-y-8">
                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
                                    
                                    <div className="space-y-6">
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-800 mb-3">a. Personal Information You Provide</h3>
                                            <p className="text-gray-700 mb-3">When you sign up for our services or make a payment, you may provide:</p>
                                            <ul className="space-y-2 text-gray-700 ml-6">
                                                <li>• Name</li>
                                                <li>• Email address</li>
                                                <li>• Billing address</li>
                                                <li>• Phone number</li>
                                                <li>• Payment method (e.g., credit card details via our payment processor)</li>
                                            </ul>
                                            <p className="text-gray-700 mt-3">
                                                <strong>Note:</strong> We do <strong>not</strong> store credit card or payment information on our servers. All billing details are securely processed through our third-party payment provider (e.g., Stripe, PayPal).
                                            </p>
                                        </div>

                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-800 mb-3">b. Automatically Collected Data</h3>
                                            <p className="text-gray-700 mb-3">When you visit our website, we may automatically collect:</p>
                                            <ul className="space-y-2 text-gray-700 ml-6">
                                                <li>• IP address</li>
                                                <li>• Browser type</li>
                                                <li>• Device information</li>
                                                <li>• Pages visited and interaction logs</li>
                                            </ul>
                                            <p className="text-gray-700 mt-3">This information is used for analytics, troubleshooting, and improving user experience.</p>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
                                    <p className="text-gray-700 mb-3">We use your information to:</p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• Provide and manage hosting services</li>
                                        <li>• Process payments via a secure third-party gateway</li>
                                        <li>• Send service-related notifications</li>
                                        <li>• Communicate about billing and support</li>
                                        <li>• Comply with legal obligations</li>
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Third-Party Services</h2>
                                    <p className="text-gray-700 mb-3">
                                        We use trusted third-party services to help operate our business, including payment processors and analytics platforms. These services may have access to your information as necessary to perform their functions, but they are not permitted to use it for other purposes.
                                    </p>
                                    <p className="text-gray-700 mb-3"><strong>Our payment processors handle:</strong></p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• Secure collection and processing of billing information</li>
                                        <li>• PCI-compliant storage and encryption</li>
                                        <li>• Fraud detection</li>
                                    </ul>
                                    <p className="text-gray-700 mt-3">
                                        We do <strong>not</strong> store your full payment details (e.g., credit card numbers) on our own systems.
                                    </p>
                                    <p className="text-gray-700 mt-3">
                                        We use Google reCAPTCHA on our contact form to help prevent spam and abuse. reCAPTCHA may collect hardware and software information, such as device and application data, and send it to Google for analysis. Use of reCAPTCHA is subject to Google's{' '}
                                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">Privacy Policy</a> and{' '}
                                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">Terms of Service</a>.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
                                    <p className="text-gray-700 mb-3">We implement industry-standard security measures to protect your information, including:</p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• SSL encryption</li>
                                        <li>• Firewall protections</li>
                                        <li>• Regular software updates</li>
                                    </ul>
                                    <p className="text-gray-700 mt-3">
                                        While we take reasonable steps to safeguard your data, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Your Rights and Choices</h2>
                                    <p className="text-gray-700 mb-3">Depending on your location, you may have the right to:</p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• Access or request a copy of your personal data</li>
                                        <li>• Correct inaccurate or incomplete information</li>
                                        <li>• Request deletion of your data (subject to legal obligations)</li>
                                        <li>• Opt out of certain communications</li>
                                    </ul>
                                    <p className="text-gray-700 mt-3">To exercise any of these rights, please contact us at [Insert Contact Email].</p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Cookies and Tracking</h2>
                                    <p className="text-gray-700 mb-3">
                                        Our website may use cookies to enhance user experience, remember login details, and gather analytics data.
                                    </p>
                                    <p className="text-gray-700">You can manage your cookie preferences through your browser settings.</p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Children's Privacy</h2>
                                    <p className="text-gray-700">
                                        Our services are not intended for children under 13. We do not knowingly collect personal data from children. If we learn we have collected such data, we will delete it promptly.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Changes to This Privacy Policy</h2>
                                    <p className="text-gray-700">
                                        We may update this policy from time to time. If we make material changes, we will notify you by posting the updated policy on our website with a new effective date.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Contact Us</h2>
                                    <p className="text-gray-700 mb-3">If you have questions or concerns about this Privacy Policy, please contact:</p>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <p className="text-gray-700">
                                            <strong>No Big Deal Group LLC</strong><br/>
                                            <a href="mailto:support@thehostbros.com">support@thehostbros.com</a><br/>
                                        </p>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PrivacyPolicyPage;
